import * as React from "react"
import { Link } from "gatsby"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"

import logo from "../images/pemalex_logo.png"
import { contactEnabled } from "../../site-config"
import { WHATSAPP_URL } from "./whatsapp"
import {
  FOOTER_ID,
  HOME_ID,
  SECTIONS,
  hasSection,
  isOnePage,
  scrollToSection,
} from "./scroll"
import * as navbarStyles from "./navbar.module.css"

/*
 * One list of links in two shapes.
 *
 * From 992px up — Bootstrap's `lg`, where this navbar has always expanded — it
 * is a row at the right of the bar. Below that the bar carries a hamburger and
 * the same list becomes the mobile menu from the handoff: a full-screen panel
 * that slides down over the page, links at display size, the address and phone
 * on a line underneath. The two shapes are one `<ul>` under a media query
 * rather than two lists, so the current-section marking and the Contato switch
 * have a single place to live.
 *
 * Open state is React's, not Bootstrap's collapse plugin's. Collapse animates
 * the height of an element that stays in normal flow, which is a different
 * thing from a fixed overlay arriving from above: it cannot produce the
 * transform, and an in-flow menu moves the document under the reader every
 * time it opens or closes. Both functions below used to carry a workaround for
 * exactly that, and neither needs one now.
 */

/* Named for the toggle's `aria-controls`. */
const MENU_ID = "nav-menu"

/* The one section that is also a brand of its own. In the mobile menu it is
   set in the blue the rest of the site uses for Amaré, per the handoff. The id
   is the one SECTIONS carries in scroll.js. */
const AMARE_ID = "amare"

/* `aria-current` is "location" rather than "page": the marked item points
   within the page the reader is already on, so it says where they are rather
   than which page they are looking at. */
const ListLink = props => {
  const classes = [props.className, props.active ? navbarStyles.active : null]
    .filter(Boolean)
    .join(" ")
  const itemClasses = [navbarStyles.navItem, props.itemClassName]
    .filter(Boolean)
    .join(" ")

  return (
    <li className={itemClasses}>
      <Link
        to={props.to}
        onClick={props.onClick}
        className={classes || undefined}
        aria-current={props.active ? "location" : undefined}
      >
        {props.children}
      </Link>
    </li>
  )
}

/*
 * The site is one scrolling page, so the nav moves between sections of `/`
 * rather than between routes. Contato is the exception — it is still a page of
 * its own, and is still an ordinary link.
 *
 * Every section item is a real `<Link to="/#id">` underneath. On the home page
 * the click is intercepted and turned into a scroll; anywhere else (Contato,
 * or one of the routes the sections are still mounted on individually) the
 * link is left alone and Gatsby navigates home, where the hash is picked up on
 * arrival by `landOnHash`.
 */
export default function Navbar() {
  const navRef = React.useRef(null)
  const [active, setActive] = React.useState(null)
  const [menuOpen, setMenuOpen] = React.useState(false)

  const closeMenu = React.useCallback(() => setMenuOpen(false), [])

  /* Esc closes the menu, wherever the focus happens to be. Only bound while it
     is open, so nothing is listening on the page the rest of the time. */
  React.useEffect(() => {
    if (!menuOpen) return undefined

    const onKeyDown = event => {
      if (event.key === "Escape") setMenuOpen(false)
    }

    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [menuOpen])

  /*
   * Publish the navbar's real height as `--nav-offset`, the token every
   * scroll target on the site reads through `scroll-margin-top`.
   *
   * It was a hand-written 76px, which is a couple of pixels more than the
   * navbar actually measures — so a section came to rest just below the
   * navbar rather than against it, and the gap showed a sliver of whatever
   * was above it. Between Produtos and Amaré that is the full-bleed
   * estamparia photo, against Amaré's dark green: very visible.
   *
   * Measured rather than corrected to a better constant. The height is the
   * logo's, which is an image with no dimensions in the markup — so the
   * navbar is short until it loads (hence `onLoad` on the image below) and
   * the right answer is not knowable from the stylesheet anyway.
   *
   * Floor, minus a pixel: the section should tuck a fraction under an opaque
   * navbar rather than risk stopping a fraction short of it and leaving a
   * hairline of the photo behind.
   */
  const syncNavOffset = React.useCallback(() => {
    const nav = navRef.current
    if (!nav) return

    /* The open menu never enters this measurement: it is a fixed overlay, so
       it is out of flow and the bar is the same height open or shut. */
    const height = nav.getBoundingClientRect().height
    if (height <= 0) return
    const offset = Math.max(0, Math.floor(height) - 1)
    document.documentElement.style.setProperty("--nav-offset", offset + "px")
  }, [])

  React.useEffect(() => {
    syncNavOffset()
    window.addEventListener("resize", syncNavOffset)
    return () => window.removeEventListener("resize", syncNavOffset)
  }, [syncNavOffset])

  /*
   * Which section the reader is in, for the highlight in the nav.
   *
   * Read off scroll position rather than an IntersectionObserver: the
   * sections are wildly different heights and several are taller than the
   * viewport, so "which one is most visible" is not the question — "which one
   * have we most recently scrolled into" is. The line it is measured against
   * is the bottom of the navbar, which is also where a section comes to rest
   * when you click through to it.
   */
  React.useEffect(() => {
    if (!isOnePage()) return undefined

    let frame = null

    const measure = () => {
      frame = null
      const line = (navRef.current ? navRef.current.offsetHeight : 0) + 12

      /* At the very bottom, the last section is the answer whether or not its
         top ever crossed the line — a short final section on a tall viewport
         can never reach it otherwise. */
      const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2

      if (atBottom) {
        setActive(SECTIONS[SECTIONS.length - 1].id)
        return
      }

      let current = HOME_ID
      SECTIONS.forEach(({ id }) => {
        const el = document.getElementById(id)
        if (el && el.getBoundingClientRect().top <= line) current = id
      })
      setActive(current)
    }

    const onScroll = () => {
      if (frame === null) frame = window.requestAnimationFrame(measure)
    }

    measure()
    window.addEventListener("scroll", onScroll, { passive: true })
    window.addEventListener("resize", onScroll)
    return () => {
      if (frame !== null) window.cancelAnimationFrame(frame)
      window.removeEventListener("scroll", onScroll)
      window.removeEventListener("resize", onScroll)
    }
  }, [])

  const goToSection = id => event => {
    if (!isOnePage()) return
    /* Let the browser have modified clicks — open-in-new-tab still works. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
    if (!hasSection(id)) return

    event.preventDefault()
    setActive(id)
    setMenuOpen(false)

    /* Keep the URL in step so the section is linkable, without pushing an
       entry per click onto the back stack. The existing state object is
       passed straight back through: Gatsby's scroll restoration keys off the
       `key` reach-router puts there, and replacing it with null loses the
       scroll position of every page already in the history stack. */
    const url = id === HOME_ID ? window.location.pathname : "#" + id
    window.history.replaceState(window.history.state, "", url)

    /* Straight into the scroll. The menu slides up over the top of it, and
       since it is a fixed overlay the document beneath never moves — so
       there is nothing to wait for before aiming at the section. */
    scrollToSection(id)
  }

  return (
    <nav ref={navRef} className="navbar sticky-top navbar-expand-lg bg-light">
      <div className="container">
        <Link
          to="/"
          onClick={goToSection(HOME_ID)}
          className={navbarStyles.brand}
        >
          {/* The logo is what makes the navbar as tall as it is, and it has
              no dimensions in the markup — so the navbar grows when this
              lands, and the offset has to be measured again. */}
          <img
            src={logo}
            alt="Logo"
            className={navbarStyles.logo}
            onLoad={syncNavOffset}
          />
        </Link>
        {/* Three bars that fold into an X. Bootstrap's own class is kept for
            the one thing still wanted from it — `display: none` from lg up —
            and everything it paints is overridden in the stylesheet. */}
        <button
          type="button"
          className={`navbar-toggler ${navbarStyles.toggle} ${
            menuOpen ? navbarStyles.open : ""
          }`}
          aria-controls={MENU_ID}
          aria-expanded={menuOpen}
          aria-label={menuOpen ? "Fechar menu" : "Abrir menu"}
          onClick={() => setMenuOpen(open => !open)}
        >
          <span className={navbarStyles.bar}></span>
          <span className={navbarStyles.bar}></span>
          <span className={navbarStyles.bar}></span>
        </button>
        <div
          id={MENU_ID}
          className={`navbar-collapse ${navbarStyles.menu} ${
            menuOpen ? navbarStyles.open : ""
          }`}
        >
          <ul className={`navbar-nav w-100 justify-content-end ${navbarStyles.links}`}>
            {SECTIONS.map(({ id, label }) => {
              /* Contato points at the footer while the form is off. Switch it
                 back on and the page below takes this item's place, rather
                 than the nav carrying two of them. See site-config.js. */
              if (id === FOOTER_ID && contactEnabled) return null

              return (
                <ListLink
                  key={id}
                  to={id === HOME_ID ? "/" : `/#${id}`}
                  onClick={goToSection(id)}
                  active={active === id}
                  className={id === AMARE_ID ? navbarStyles.amare : undefined}
                  /* The bar only. The panel answers the same question with a
                     WhatsApp button, which is the better one to hand someone
                     already holding a phone. */
                  itemClassName={id === FOOTER_ID ? navbarStyles.barOnly : undefined}
                >
                  {label}
                </ListLink>
              )
            })}
            {/* The one item that is a page rather than a section — and the
                one that can be switched off. See site-config.js. */}
            {contactEnabled && (
              <ListLink to="/contact/" onClick={closeMenu}>Contato</ListLink>
            )}
          </ul>
          {/* Where the factory is and how to reach it, closing the menu the
              way the footer closes the page. Both are shown only while the
              list is a panel; from lg up the stylesheet takes them out. */}
          <p className={navbarStyles.menuFoot}>Maracanaú, Ceará</p>
          <a
            className={navbarStyles.menuWhats}
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={closeMenu}
          >
            <FontAwesomeIcon
              icon={faWhatsapp}
              className={navbarStyles.menuWhatsIcon}
            />
            Falar no WhatsApp
          </a>
        </div>
      </div>
    </nav>
  )
}
